import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminCriticalityApi, settingsApi, oesApi } from '../../services/api';
import { getCriticalityLabels } from '../../utils/settingsScales';
import { useInstanceMode } from '../../providers/InstanceModeProvider';
import Layout from '../../components/layout/Layout';
import { useLabels } from '../../providers/LabelsProvider';
import { useT } from '../../providers/I18nProvider';
import ConfirmModal from '../../components/ConfirmModal';

const TYPE_LABELS = { criticality: 'Criticality', posture: 'Posture', risk: 'Risk' };

const CRIT_BADGE_BG = {
  ma: 'bg-red-50 text-red-700 ring-red-100',
  a: 'bg-orange-50 text-orange-700 ring-orange-100',
  m: 'bg-amber-50 text-amber-700 ring-amber-100',
  b: 'bg-blue-50 text-blue-700 ring-blue-100',
  mb: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
};

// Color coding based on compliance score
const getComplianceColor = (score) => {
  if (score >= 80) return '#10b981';
  if (score >= 50) return '#f59e0b';
  return '#ef4444';
};

// Posture compliance bar: slim progress bar with label
function PostureComplianceBar({ compliance }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const id = setTimeout(() => setWidth(compliance), 50);
    return () => clearTimeout(id);
  }, [compliance]);
  const color = getComplianceColor(compliance);
  return (
    <div className="flex items-center gap-2 w-28">
      <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700 ease-out"
          style={{ width: `${width}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-[11px] font-bold text-slate-500 tabular-nums w-8 text-right">{compliance}%</span>
    </div>
  );
}

// Icons as inline SVG
const IconChecklist = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
  </svg>
);
const IconEdit = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);
const IconTrash = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

export default function CompletedAssessmentsList() {
  const labels = useLabels();
  const t = useT();
  const navigate = useNavigate();
  const { pureOES } = useInstanceMode();
  const [myOesId, setMyOesId] = useState(null);
  const [criticalityList, setCriticalityList] = useState([]);
  const [postureList, setPostureList] = useState([]);
  const [settingsByOrg, setSettingsByOrg] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // In OES mode, find the single OES ID for filtering
  useEffect(() => {
    if (pureOES) {
      oesApi.getAll().then(r => {
        const list = r.data || [];
        if (list.length > 0) setMyOesId(list[0].id);
      }).catch(() => {});
    }
  }, [pureOES]);

  const fetchLists = async () => {
    try {
      setLoading(true);
      setError(null);
      const [critRes, postureRes] = await Promise.all([
        adminCriticalityApi.getCompletedAssessments(!showAll),
        adminCriticalityApi.getCompletedPostureAssessments(),
      ]);
      let crit = critRes.data || [];
      let posture = postureRes.data || [];
      // OES mode: filter to single OES only
      if (pureOES && myOesId) {
        crit = crit.filter(c => c.oes_id === myOesId);
        posture = posture.filter(p => p.oes_id === myOesId);
      }
      setCriticalityList(crit);
      setPostureList(posture);

      const orgIds = [...new Set(crit.map((c) => c.organization_id).filter(Boolean))];
      const settings = {};
      await Promise.all(
        orgIds.map(async (orgId) => {
          try {
            const res = await settingsApi.get('organization', orgId);
            settings[orgId] = res.data || {};
          } catch {
            settings[orgId] = {};
          }
        })
      );
      setSettingsByOrg(settings);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load completed assessments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLists();
  }, [showAll]);

  const unifiedRows = [
    ...criticalityList.map((item) => {
      const orgId = item.organization_id;
      const orgScales = (settingsByOrg[orgId] || {}).scales;
      const critLabels = getCriticalityLabels(orgScales);
      const critKey = item.oes_criticality?.toLowerCase();
      return {
        type: 'criticality',
        id: item.id,
        oes_id: item.oes_id,
        assessment_id: item.assessment_id,
        organization_id: orgId,
        oes_name: item.oes_name,
        assessment_name: item.assessment_name,
        oes_criticality: item.oes_criticality,
        critKey,
        resultDisplay: critLabels[critKey] || item.oes_criticality,
        date: item.assessment_date,
        assessed_by_name: item.assessed_by_name,
      };
    }),
    ...postureList.map((item) => ({
      type: 'posture',
      id: item.id,
      oes_id: item.oes_id,
      assessment_id: item.assessment_id,
      oes_name: item.oes_name,
      assessment_name: item.assessment_name,
      resultDisplay: `${item.gen_compliance}% compliance`,
      resultClass: 'bg-eucnet-sky/10 text-eucnet-navy',
      compliance: item.gen_compliance || 0, // Store compliance percentage for progress bar
      date: item.submitted_at,
      assessed_by_name: item.assessed_by_name || '—',
    })),
  ];

  const openCriticalityResult = (assessmentId) => {
    navigate(`/admin/assessment-results/${assessmentId}`);
  };

  const openCriticalityEdit = (oesId) => {
    navigate(`/oes/${oesId}/assessment`, { state: { loadExisting: true, fromAdminList: true } });
  };


  const openPostureResult = (oesId) => {
    navigate(`/oes/${oesId}/questionnaire-result`);
  };

  const openPostureEdit = (oesId) => {
    navigate(`/oes/${oesId}/questionnaire`, { state: { loadExisting: true, fromAdminList: true } });
  };

  const handleDelete = (item) => {
    setDeleteConfirm(item);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      if (deleteConfirm.type === 'criticality') {
        await adminCriticalityApi.deleteCriticalityResults(deleteConfirm.id);
      } else if (deleteConfirm.type === 'posture') {
        await oesApi.deleteQuestionnaireResponse(deleteConfirm.oes_id, deleteConfirm.assessment_id);
      }
      setDeleteConfirm(null);
      await fetchLists();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{t('nav.assessmentResults')}</h1>
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <span className="text-xs font-medium text-slate-500">{t('common.showHistory')}</span>
            <div className="relative">
              <input type="checkbox" className="sr-only peer" checked={showAll} onChange={() => setShowAll(v => !v)} />
              <div className="w-9 h-5 bg-slate-200 peer-checked:bg-eucnet-navy rounded-full after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full" />
            </div>
          </label>
        </div>

        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">{t('common.loading')}</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {!loading && !error && unifiedRows.length === 0 && (
          <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
            {t('admin.noCompletedAssessments')}
          </div>
        )}

        {!loading && !error && unifiedRows.length > 0 && (
          <div className="bg-white rounded-xl shadow overflow-hidden overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('common.type')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{labels.entityName}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('common.assessment')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('common.result')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('admin.assessedBy')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('common.date')}</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {unifiedRows.map((row) => (
                  <tr key={`${row.type}-${row.id}`} className="hover:bg-gray-50">
                    <td className="px-4 py-4 text-sm font-medium text-gray-700">{TYPE_LABELS[row.type]}</td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">{row.oes_name || '—'}</td>
                    <td className="px-4 py-4 text-sm text-gray-700" title={row.assessment_name || ''}>
                      {(row.assessment_name || '—').slice(0, 32)}{(row.assessment_name || '').length > 32 ? '…' : ''}
                    </td>
                    <td className="px-4 py-4">
                      {row.type === 'posture' ? (
                        <PostureComplianceBar compliance={row.compliance} />
                      ) : (
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-bold ring-1 ${CRIT_BADGE_BG[row.critKey] || 'bg-slate-50 text-slate-600 ring-slate-200'}`}>
                          {row.resultDisplay}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">{row.assessed_by_name || '—'}</td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {row.date ? new Date(row.date).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-4 text-right">
                      {row.type === 'criticality' && (
                        <div className="inline-flex items-center justify-end gap-0.5" aria-label="Row actions">
                          <button
                            onClick={() => openCriticalityResult(row.id)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                            title="View scorecard"
                          >
                            <IconChecklist />
                          </button>
                          <button
                            onClick={() => openCriticalityEdit(row.oes_id)}
                            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                            title="Edit assessment"
                          >
                            <IconEdit />
                          </button>
                          <button
                            onClick={() => handleDelete(row)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                            title="Delete results"
                          >
                            <IconTrash />
                          </button>
                        </div>
                      )}
                      {row.type === 'posture' && (
                        <div className="inline-flex items-center gap-2">
                          <span className="inline-flex items-center gap-0.5" aria-label="Row actions">
                            <button
                              onClick={() => openPostureResult(row.oes_id)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                              title="View result"
                            >
                              <IconChecklist />
                            </button>
                            <button
                              onClick={() => openPostureEdit(row.oes_id)}
                              className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                              title="Edit questionnaire"
                            >
                              <IconEdit />
                            </button>
                            <button
                              onClick={() => handleDelete(row)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                              title="Delete results"
                            >
                              <IconTrash />
                            </button>
                          </span>
                        </div>
                      )}
                      {row.type === 'risk' && <span className="text-gray-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        open={!!deleteConfirm}
        title={deleteConfirm?.type === 'posture' ? t('admin.deletePostureResults') : t('admin.deleteCriticalityResults')}
        message={deleteConfirm?.type === 'posture'
          ? t('admin.deletePostureMsg', { entity: labels.entityName })
          : t('admin.deleteCriticalityMsg', { entity: labels.entityName })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm(null)}
      />
    </Layout>
  );
}
